import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import {
  isFreeGiftParentProduct,
  addParentWithFreeGifts,
  ensureFreeGiftTimer,
} from '@theme/free-gift-bundle';

/**
 * @typedef {object} MercadopagoCheckoutRefs
 * @property {HTMLButtonElement} button
 */

/**
 * Direct checkout button styled for Mercado Pago.
 *
 * @extends {Component<MercadopagoCheckoutRefs>}
 */
class MercadopagoCheckoutButton extends Component {
  requiredRefs = ['button'];

  connectedCallback() {
    super.connectedCallback();
    const section = this.closest('.shopify-section, dialog');
    section?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const section = this.closest('.shopify-section, dialog');
    section?.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
  }

  /** @param {VariantUpdateEvent} event */
  #handleVariantUpdate = (event) => {
    const variant = event.detail.resource;
    if (!variant) return;

    const isAvailable = Boolean(variant.available);
    this.refs.button.disabled = !isAvailable;
    this.dataset.disabled = isAvailable ? 'false' : 'true';
  };

  handleClick = async () => {
    const productForm = /** @type {HTMLElement | null} */ (this.closest('product-form-component'));
    if (!productForm || this.refs.button.disabled) return;

    const variantInput = /** @type {HTMLInputElement | null} */ (productForm.querySelector('input[name="id"]'));
    const quantitySelector = /** @type {{ getValue?: () => string } | null} */ (
      productForm.querySelector('quantity-selector-component')
    );

    const variantId = variantInput?.value;
    const quantity = Number(quantitySelector?.getValue?.()) || Number(productForm.dataset.quantityDefault) || 1;

    if (!variantId) return;

    this.refs.button.disabled = true;

    try {
      await fetch('/cart/clear.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
      });

      const items = isFreeGiftParentProduct(productForm.dataset.productId)
        ? null
        : [{ id: Number(variantId), quantity }];

      let data;
      if (items) {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ items }),
        });
        data = await response.json();
        if (!response.ok || data.status) {
          throw new Error(data.message || data.description || 'No se pudo iniciar el checkout.');
        }
      } else {
        data = await addParentWithFreeGifts(Number(variantId), quantity);
        if (data.status) {
          throw new Error(data.message || data.description || 'No se pudo iniciar el checkout.');
        }
        await ensureFreeGiftTimer();
      }

      window.location.href = '/checkout';
    } catch (error) {
      console.error(error);
      const addToCartButton = /** @type {HTMLButtonElement | null} */ (
        productForm.querySelector('[ref="addToCartButton"]')
      );
      this.refs.button.disabled = addToCartButton?.disabled ?? false;
    }
  };
}

if (!customElements.get('mercadopago-checkout-button')) {
  customElements.define('mercadopago-checkout-button', MercadopagoCheckoutButton);
}
